import React from 'react';
import cs from 'classnames';
import { Popover, PlacementType } from '../popover/popover.tsx';
import { Menu, MenuItemProps } from '@kim/ui';
import { flushSync } from 'react-dom';

const DEFAULT_CLASSNAME = 'k-ui-dropdown';

export type DropDownItemProps = Omit<
  MenuItemProps,
  'onClickItemCallback' | 'onMouseEnterCallback'
>;

export interface DropdownProps {
  /**
   * 菜单配置项
   */
  dropList: DropDownItemProps[];
  /**
   * 弹出位置
   */
  placement?: PlacementType;
  /**
   * popper偏移
   */
  offset?: [number, number];
  /**
   * 触发target
   */
  children: React.ReactNode;
  /**
   * 每一个item点击事件回调
   */
  clickItemCallBack?: (item: MenuItemProps, index: number) => void;
  /**
   * menu的className
   */
  className?: string;
  /**
   * dropdown的className
   */
  dropdownClassName?: string;
  /**
   * 菜单是否显示的回调处理
   */
  onVisibleChange?: (visible: boolean) => void;
  /**
   * 触发方式，默认click
   * hover: 鼠标移入触发
   * click: 点击触发
   * 右键出触发
   */
  trigger?: 'click' | 'hover' | 'rightClick';
  subTrigger?: 'click' | 'hover' | 'rightClick';
  /**
   * 最大高度
   */
  maxHeight?: number;
  /**
   * 宽度
   */
  width?: number;

  /**
   * popover 的 z-index
   */
  popoverZIndex?: number;

  /**
   * 二级菜单改变回调
   */
  onSubmenuChange?: (
    visible: boolean,
    item: MenuItemProps,
    closeAllMenu?: boolean,
  ) => void;
  delay?: number;
  draggableConfig?: any;
  /**
   * click 事件
   * 与 trigger 为 rightClick 配合使用
   */
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

export const Dropdown: React.FC<DropdownProps> = ({
  dropList,
  placement,
  offset,
  children,
  className,
  dropdownClassName,
  popoverZIndex,
  onVisibleChange,
  maxHeight,
  width,
  trigger = 'click',
  subTrigger = 'hover',
  clickItemCallBack,
  onSubmenuChange,
  draggableConfig,
  delay,
  onClick,
}) => {
  const [visible, setVisible] = React.useState(false);
  const dropdownRef = React.useRef(null);
  const menuMouseEnter = React.useRef(false);
  const targetMouseEnter = React.useRef(false);
  let mouseOutTimer: null | NodeJS.Timeout = null;
  let mouseInTimer: null | NodeJS.Timeout = null;
  const isHover = trigger === 'hover';

  const showMenu = () => {
    setVisible(true);

    onVisibleChange && onVisibleChange(true);
  };

  const closeMenu = () => {
    setVisible(false);
    onVisibleChange && onVisibleChange(false);
  };

  // hover情况下，关闭menu加一个延迟
  const closeMenuDelay = () => {
    mouseOutTimer = setTimeout(() => {
      if (!menuMouseEnter.current) {
        menuMouseEnter.current = false;
        closeMenu();
      }
    }, 300);
  };

  // 点击触发 delay
  const handleTriggerClick = () => {
    if (!delay) {
      showMenu();
      return;
    }
    if (mouseInTimer) {
      clearTimeout(mouseInTimer);
    }
    targetMouseEnter.current = true;
    mouseInTimer = setTimeout(() => {
      targetMouseEnter.current && showMenu();
    }, delay);
  };

  const handleClickAway = () => {
    closeMenu();
  };

  const handleMouseLeave = () => {
    targetMouseEnter.current = false;
    if (mouseInTimer && isHover) {
      clearTimeout(mouseInTimer);
    }
    if (!menuMouseEnter.current && isHover) {
      return closeMenuDelay();
    }
  };

  const _clickItemCallBack = (item: MenuItemProps, index: number) => {
    if (item.subMenuConfig) {
      clickItemCallBack && clickItemCallBack(item, index);
      return;
    }
    if (!item.notCloseMenu) {
      closeMenu();
    }

    clickItemCallBack && clickItemCallBack(item, index);
  };

  const handleMenuMouseIsEnter = (isIn: boolean) => {
    menuMouseEnter.current = isIn;
    if (isHover && menuMouseEnter.current === false) {
      closeMenuDelay();
    }
  };
  const handleSubmenuClick = (
    item: MenuItemProps,
    index: number,
    closeAllMenu?: boolean,
  ) => {
    if (closeAllMenu) {
      closeMenu();
    }
  };

  React.useEffect(() => {
    return () => {
      if (mouseOutTimer) {
        clearTimeout(mouseOutTimer);
      }
    };
  }, []);

  const _dropDownCls = cs(`${DEFAULT_CLASSNAME}`, dropdownClassName);

  return (
    <React.Fragment>
      {trigger === 'rightClick' && (
        <div
          {...(draggableConfig || {})}
          ref={dropdownRef}
          className={_dropDownCls}
          onContextMenu={handleTriggerClick}
          onClick={onClick}
        >
          {children}
        </div>
      )}
      {trigger === 'click' && (
        <div
          ref={dropdownRef}
          className={_dropDownCls}
          onClick={handleTriggerClick}
        >
          {children}
        </div>
      )}
      {isHover && (
        <div
          ref={dropdownRef}
          className={_dropDownCls}
          onMouseEnter={handleTriggerClick}
          onMouseLeave={handleMouseLeave}
        >
          {children}
        </div>
      )}
      <Popover
        needMask={!isHover}
        onClickAway={handleClickAway}
        visible={visible}
        placement={placement}
        zIndex={popoverZIndex}
        content={
          <Menu
            className={className}
            menuList={dropList}
            clickItemCallBack={_clickItemCallBack}
            onMouseIsEnter={handleMenuMouseIsEnter}
            maxHeight={maxHeight}
            width={width}
            onSubmenuChange={onSubmenuChange}
            trigger={subTrigger || 'hover'}
            onSubmenuClick={handleSubmenuClick}
          />
        }
        offset={offset}
        refEle={dropdownRef.current}
      />
    </React.Fragment>
  );
};
